import { Table, TablePaginationConfig, TableProps } from 'antd';
import {
    FilterValue,
    Key,
    RowSelectMethod,
    SelectionSelectFn,
    SorterResult,
    TableCurrentDataSource,
} from 'antd/es/table/interface';
import React, { useContext, useRef } from 'react';
import { useRelayEnvironment } from 'react-relay';
import { OperationType } from 'relay-runtime';
import styled from 'styled-components';

import { getObjectFromRelayStoreById, hashString } from '../functions';
import useAccessForItem from '../hooks/useAccessForItem';
import useDispatchSetterStore from '../hooks/useDispatchSetterStore';
import useDynamicSelector from '../hooks/useDynamicSelector';
import { SmxPropsTableContext } from '../providers/propsTableProvider';
import { getSetterTable, ITableState } from '../store/slices/tableSlice';

type TableRowSelection<T extends object = object> = TableProps<T>['rowSelection'];

const TableStyled = styled(Table)`
    table {
        //table-layout: fixed !important;
        width: 100%;
    }

    a {
        color: inherit;
    }
`;

export interface ITableDbBody<TQuery extends OperationType> {
    newColumns: any;
    dataRows: any[];
    onClickRow?: (data: any, dataRaw: any) => void;
    heightTableBody?: number;
    onSelectionChange?: (
        selectedRowKeys: Key[],
        selectedRows: TQuery[],
        info: {
            type: RowSelectMethod;
        },
    ) => void;
    onSelect?: SelectionSelectFn<TQuery>;
}

const TableDbBody = <TQuery extends OperationType>({
    newColumns,
    dataRows,
    heightTableBody,
    onClickRow,
    onSelect,
    onSelectionChange,
}: ITableDbBody<TQuery>) => {
    const environment = useRelayEnvironment();
    const tableRef = useRef();

    const { nameQuery, prefixStore } = useContext(SmxPropsTableContext);
    const setterTable = useDispatchSetterStore<ITableState>(getSetterTable(nameQuery + prefixStore));

    const heightControlPanel = useDynamicSelector('table', 'heightControlPanel');
    const heightPaginationPanel = useDynamicSelector('table', 'heightPaginationPanel');

    const handleTableChange = (
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue | null>,
        sorterTable: SorterResult<any> | SorterResult<any>[],
        _extra: TableCurrentDataSource<any>,
    ) => {
        const newSorter: string[] = [];

        const pushNewValue = (sorterColumn: SorterResult<any>) => {
            newSorter.push(`${sorterColumn.columnKey}:${sorterColumn.order === 'ascend' ? 'ASC' : 'DESC'}`);
        };

        if (Array.isArray(sorterTable)) {
            sorterTable.forEach((sorter) => {
                pushNewValue(sorter);
            });
        } else if (sorterTable.order) {
            pushNewValue(sorterTable);
        }

        setterTable({ sorter: newSorter });
    };

    const handlerOnRow = (record: any) => {
        const access = useAccessForItem(record, ['work-item.all.view', 'work-item.owner', 'work-item.assignee']);

        const handleCLick =
            onClickRow && access
                ? () => {
                      console.log('click on row', record);
                      const dataRaw = getObjectFromRelayStoreById(environment, (record as any).keyRaw);
                      onClickRow(record, dataRaw);
                  }
                : undefined;

        return {
            onClick: handleCLick,
        };
    };

    const heightHeaderTable =
        (tableRef?.current?.[Object.keys(tableRef.current)[0]] as any)?.ref.current?.querySelector('.ant-table-thead')
            .offsetHeight || 0;

    const rowSelection: TableRowSelection<any> = {
        onChange: onSelectionChange
            ? (selectedRowKeys, selectedRows, info) => {
                  onSelectionChange(selectedRowKeys, selectedRows, info);
              }
            : undefined,
        onSelect: onSelect
            ? (record, selected, selectedRows, nativeEvent) => {
                  onSelect(record, selected, selectedRows, nativeEvent);
              }
            : undefined,
        // onSelectAll: (selected, selectedRows, changeRows) => {
        //     console.log(selected, selectedRows, changeRows);
        // },
    };

    return (
        <TableStyled
            ref={tableRef as any}
            key={hashString(JSON.stringify(newColumns))}
            columns={newColumns}
            dataSource={dataRows}
            bordered
            onChange={handleTableChange}
            pagination={false}
            size="small"
            scroll={{
                x: 'max-content',
                y: heightTableBody
                    ? heightTableBody - heightControlPanel - heightPaginationPanel - heightHeaderTable
                    : undefined,
            }}
            onRow={handlerOnRow}
            rowSelection={onSelectionChange || onSelect ? rowSelection : undefined}
        />
    );
};

export default TableDbBody;
